import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import marketplaceService from '../../../services/marketplaceService';
import { auctionAlertService } from '../../../services/auctionAlertService';
import auctionEmailService from '../../../services/auctionEmailService';
import liveNotificationService from '../../../services/liveNotificationService';

const AuctionDetailModal = ({ auction, onClose, onBidPlaced }) => {
  const { user } = useAuth();
  const [showBidForm, setShowBidForm] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bids, setBids] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState('');

  const isOwnAuction = auction?.seller_id === user?.id;
  const minBid = parseFloat(auction?.current_bid || auction?.starting_bid || 0) + parseFloat(auction?.bid_increment || 10);

  const updateTimeRemaining = () => {
    const now = new Date();
    const end = new Date(auction?.end_date);
    const diff = end - now;

    if (diff <= 0) {
      setTimeRemaining('Auction Ended');
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) {
      setTimeRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    } else if (hours > 0) {
      setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
    } else {
      setTimeRemaining(`${minutes}m ${seconds}s`);
    }
  };

  useEffect(() => {
    loadBids();
    const timer = setInterval(updateTimeRemaining, 1000);
    return () => clearInterval(timer);
  }, [auction]);

  const loadBids = async () => {
    const { data } = await marketplaceService?.getAuctionBids(auction?.id);
    setBids(data || []);
  };

  const handlePlaceBid = async (e) => {
    e?.preventDefault();
    
    const amount = parseFloat(bidAmount);
    if (isNaN(amount) || amount < minBid) {
      return;
    }

    setSubmitting(true);

    const bidData = {
      auction_id: auction?.id,
      bidder_id: user?.id,
      bid_amount: parseFloat(bidAmount),
      currency: auction?.currency || 'USD'
    };

    const { data, error } = await marketplaceService?.placeBid(bidData);
    
    if (!error) {
      const auctionTitle = auction?.title || auction?.collectible_name;
      const newBidAmount = parseFloat(bidAmount);
      const previousHighBidder = bids?.[0]?.bidder_id;

      // Notify the previous highest bidder they've been outbid
      if (previousHighBidder && previousHighBidder !== user?.id) {
        auctionAlertService?.sendOutbidAlert(previousHighBidder, {
          id: auction?.id,
          title: auctionTitle,
          currentBid: newBidAmount,
          yourBid: parseFloat(bids?.[0]?.bid_amount || 0)
        });
        // Live in-app notification: outbid
        liveNotificationService?.createNotification({
          userId: previousHighBidder,
          notificationType: 'outbid',
          title: "You've been outbid!",
          message: `Someone placed a higher bid of $${newBidAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })} on "${auctionTitle}". Bid now to stay in the lead!`,
          auctionId: auction?.id,
          metadata: { currentBid: newBidAmount, previousBid: parseFloat(bids?.[0]?.bid_amount || 0) }
        });
        // Send outbid email to previous highest bidder
        auctionEmailService?.sendOutbidEmail(previousHighBidder, {
          title: auctionTitle,
          yourBid: parseFloat(bids?.[0]?.bid_amount || 0),
          currentBid: newBidAmount
        });
      }

      // Notify auction seller of bid change
      if (auction?.seller_id && auction?.seller_id !== user?.id) {
        auctionAlertService?.sendBidChangedAlert(auction?.seller_id, {
          id: auction?.id,
          title: auctionTitle,
          currentBid: newBidAmount,
          previousBid: parseFloat(auction?.current_bid || auction?.starting_bid || 0)
        });
      }

      // Live in-app notification: bid placed confirmation for the bidder
      liveNotificationService?.createNotification({
        userId: user?.id,
        notificationType: 'bid_placed',
        title: 'Bid placed successfully!',
        message: `Your bid of $${newBidAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })} on "${auctionTitle}" is now the leading bid.`,
        auctionId: auction?.id,
        metadata: { bidAmount: newBidAmount }
      });

      // Send bid placed confirmation email to the bidder
      auctionEmailService?.sendBidPlacedEmail(user?.id, {
        title: auctionTitle,
        bidAmount: newBidAmount,
        endDate: auction?.end_date
      });

      alert('Bid placed successfully!');
      setShowBidForm(false);
      setBidAmount('');
      loadBids();
      onBidPlaced?.();
    } else {
      alert('Failed to place bid: ' + error);
    }
    
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="auction-modal-title">
      <div className="bg-card border border-border rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Gavel" size={20} className="text-primary" />
            </div>
            <div>
              <h2 id="auction-modal-title" className="text-xl font-bold text-card-foreground">Auction Details</h2>
              <p className="text-sm text-muted-foreground">
                {auction?.auction_type === 'premiere' ? 'Premiere Auction' : 'Regular Auction'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close auction details"
            className="w-11 h-11 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/20"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Image & Details */}
            <div className="space-y-6">
              {/* Image */}
              <div className="aspect-square bg-muted rounded-lg overflow-hidden">
                {auction?.featured_image_url || auction?.collectible?.primary_image_url ? (
                  <img
                    src={auction?.featured_image_url || auction?.collectible?.primary_image_url}
                    alt={auction?.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Image" size={64} className="text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* Item Details */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Description</h3>
                  <p className="text-foreground">{auction?.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Condition</h3>
                    <p className="text-foreground capitalize">{auction?.condition?.replace('-', ' ')}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Shipping</h3>
                    <p className="text-foreground">${parseFloat(auction?.shipping_cost || 0)?.toFixed(2)}</p>
                  </div>
                </div>
                {auction?.category && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Category</h3>
                    <p className="text-foreground">{auction?.category?.name}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Bidding */}
            <div className="space-y-6">
              {/* Title */}
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">{auction?.title}</h1>
                {auction?.auction_type === 'premiere' && (
                  <div className="inline-flex items-center space-x-2 bg-yellow-500/10 text-yellow-600 px-3 py-1 rounded-full text-sm font-medium">
                    <Icon name="Star" size={14} />
                    <span>Premiere Auction</span>
                  </div>
                )}
              </div>

              {/* Time Remaining */}
              <div className="bg-primary/10 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={20} className="text-primary" />
                    <span className="text-sm font-medium text-muted-foreground">Time Remaining</span>
                  </div>
                  <span className="text-xl font-bold text-primary">{timeRemaining}</span>
                </div>
              </div>

              {/* Current Bid */}
              <div className="bg-card border-2 border-primary rounded-lg p-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Current Bid</p>
                  <p className="text-4xl font-bold text-primary mb-2">
                    ${parseFloat(auction?.current_bid || auction?.starting_bid || 0)?.toFixed(2)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {auction?.bid_count || 0} bid{auction?.bid_count !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Bid Form */}
              {!isOwnAuction && auction?.status === 'active' && (
                <div className="space-y-4">
                  {!showBidForm ? (
                    <Button
                      onClick={() => setShowBidForm(true)}
                      iconName="Gavel"
                      iconPosition="left"
                      size="lg"
                      fullWidth
                      className="min-h-[52px] text-base"
                    >
                      Place Bid
                    </Button>
                  ) : (
                    <form onSubmit={handlePlaceBid} className="space-y-4" noValidate aria-label="Place bid form">
                      <div>
                        <Input
                          label={`Your Bid (Minimum: $${minBid?.toFixed(2)})`}
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min={minBid?.toFixed(2)}
                          required
                          autoFocus
                          value={bidAmount}
                          onChange={(e) => setBidAmount(e?.target?.value)}
                          placeholder={minBid?.toFixed(2)}
                          aria-describedby="bid-increment-hint"
                        />
                        <p id="bid-increment-hint" className="text-xs text-muted-foreground mt-1">
                          Bid increment: ${parseFloat(auction?.bid_increment || 10)?.toFixed(2)}
                        </p>
                      </div>

                      <div className="flex space-x-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowBidForm(false);
                            setBidAmount('');
                          }}
                          fullWidth
                          className="min-h-[48px]"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          loading={submitting}
                          disabled={submitting || !bidAmount || parseFloat(bidAmount) < minBid}
                          iconName="Gavel"
                          iconPosition="left"
                          fullWidth
                          className="min-h-[48px]"
                          aria-label={`Submit bid of $${parseFloat(bidAmount || 0)?.toFixed(2)}`}
                        >
                          Submit Bid
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {isOwnAuction && (
                <div className="bg-muted/20 rounded-lg p-4 text-center">
                  <Icon name="Info" size={20} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">This is your auction</p>
                </div>
              )}

              {/* Bid History */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
                  <Icon name="History" size={20} />
                  <span>Bid History</span>
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {bids?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No bids yet</p>
                  ) : (
                    bids?.map((bid, index) => (
                      <div
                        key={bid?.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          bid?.is_winning ? 'bg-primary/10 border border-primary' : 'bg-muted/20'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-primary">#{index + 1}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {bid?.bidder?.full_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(bid?.created_at)?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-primary">
                            ${parseFloat(bid?.bid_amount)?.toFixed(2)}
                          </p>
                          {bid?.is_winning && (
                            <p className="text-xs text-primary font-medium">Winning</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuctionDetailModal;